import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Button } from 'components/Button/Button'
import InfoModal from 'components/InfoModal/InfoModal'
import { useState } from 'react'
import Styles from './DeactivateUser.module.scss'
import { UserClient } from 'api/clients/UserClient'
import { useSession } from 'hooks/useSession'
import { useModals } from 'almanac-ui/Modal'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import { AlertTriangle } from 'blaze-ui/icons'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useRootStore } from 'stores/RootStore'
import { useWorkspaceUsers } from 'hooks/queries/companies/users/useWorkspaceUsers'
import { HOME_PATH } from 'paths'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export const DeactivateUser = () => {
  const tierUsageStore = useRootStore().tierUsageStore
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false)
  const { logout } = useSession()
  const { closeAllModals, closeModal, openModal } = useModals()
  const plan = useCurrentPlan()
  const workspace = useCurrentWorkspace()
  const { data: workspaceUsers } = useWorkspaceUsers({ workspaceKey: workspace?.key })
  const isNewChurnFlow = useFlipperFlag('new_churn_flow')
  const isAutoPilot = plan.data?.plan !== PaymentPlanKey.VirtualMarketer

  // Check if user is the sole admin of a multi-member workspace
  const isSoleAdminOfMultiMemberWorkspace = (() => {
    if (!workspace || !workspaceUsers) return false
    if (workspace.members_count <= 1) return false
    if (!workspace.admin) return false
    // Count admins in the workspace - if there's only one, it's this user
    // Note: workspaceUsers may not have role info, so we use workspace.admin as a proxy
    // This is a conservative check - if admin && members > 1, show warning
    return true
  })()

  const handleDeleteAccount = () => {
    UserClient.deactivate().then((e) => {
      if (e.type === SUCCESS_RESPONSE_TYPE) {
        closeAllModals()
        logout({ logoutRedirect: HOME_PATH })
        sendMessageToNativeApp('delete_account_complete')
      } else {
        showErrorMessageToast('Something went wrong, please try again later')
      }
    })
  }

  const handleDeactivateUser = () => {
    const isSubscriptionCancelledOrTrialing =
      !!plan?.data?.cancel_at || ['trialing', 'canceled'].includes(plan?.data?.status || '')

    if (!isSubscriptionCancelledOrTrialing && isNewChurnFlow) {
      if (isAutoPilot && workspace?.id) {
        openModal(UpgradePlanModal, {
          initialPage: 'beforeDeleteAccount',
          onClose: () => {
            closeModal()
            tierUsageStore.refetch(workspace?.id)
          },
          showSuccessModal: false,
          isDeletingAccount: true,
          onDeleteAccount: handleDeleteAccount,
          isSoleAdminOfMultiMemberWorkspace,
        })
      } else {
        // No change for copilot - show regular confirmation
        setDeactivateModalVisible(true)
      }

      return
    } else {
      setDeactivateModalVisible(true)
    }
  }

  return (
    <>
      <InfoModal
        onOk={() => {
          handleDeleteAccount()
          return true
        }}
        okText="Yes, delete my account"
        cancelText="No, cancel"
        title={`Delete your account`}
        visible={deactivateModalVisible}
        setVisible={setDeactivateModalVisible}
      >
        Are you sure you want to delete your account? All your data will be removed. This action cannot be undone.
        Please note this action will not cancel any active subscriptions you have.
      </InfoModal>
      <Button
        className={Styles.deactivateButton}
        size="md"
        variant="secondary"
        onPress={handleDeactivateUser}
        data-testid="deactivate"
        isDisabled={!plan.isFetched}
        frontIcon={AlertTriangle}
      >
        Delete Account
      </Button>
    </>
  )
}
